import { computed, inject, Injectable } from '@angular/core';

import { MatSnackBar } from '@angular/material/snack-bar';

import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { delay, pipe, switchMap, tap } from 'rxjs';

import {
  WAITING_ON_YOU_SIGNAL_STATE,
  DashboardWaitingOnYouRequestParams,
  DashboardWaitingOnYouResponse,
} from '../../types';
import { DashboardService } from '../../services';
import { WAITING_ON_YOU_DEFAULT_SIGNAL_STATE } from '@data-access/dash-board/constants';
import { tapResponse } from '@ngrx/operators';

export const ON_YOU_SIGNAL_STATE = signalState<WAITING_ON_YOU_SIGNAL_STATE>({
  ...WAITING_ON_YOU_DEFAULT_SIGNAL_STATE,
});

@Injectable()
export class WaitingOnYouSignalState {
  private readonly _dashboardService = inject(DashboardService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _onYouSignalState = ON_YOU_SIGNAL_STATE;

  readonly $data = computed<DashboardWaitingOnYouResponse | null>(() =>
    this._onYouSignalState.data(),
  );
  readonly $isLoading = computed(() => this._onYouSignalState.isLoading());
  readonly $error = computed(() => this._onYouSignalState.error());

  /**
   * Loads the "Waiting on you" items into state.
   *
   * @param requestParams - Filter/paging parameters for the request.
   * @param useMock - Forwarded to `DashboardService.getWaitingOnYouItems`;
   * when true the service serves its typed mock payload instead of calling
   * the API. Driven by the card component's `mockToggle` input.
   */
  getWaitingOnYouStates = rxMethod<{
    requestParams: DashboardWaitingOnYouRequestParams;
    useMock?: boolean;
  }>(
    pipe(
      tap(() =>
        patchState(this._onYouSignalState, { isLoading: true, error: null }),
      ),
      switchMap(({ requestParams, useMock }) =>
        this._dashboardService
          .getWaitingOnYouItems(requestParams, useMock === true)
          .pipe(
            delay(250),
            tapResponse({
              error: (error: Error) =>
                patchState(this._onYouSignalState, {
                  error: error,
                  isLoading: false,
                }),
              next: (response) => {
                patchState(this._onYouSignalState, {
                  error: null,
                  isLoading: false,
                  data: response,
                });
              },
            }),
          ),
      ),
    ),
  );

  /**
   * @NOTE: PUBLIC METHODS
   */
  resetState(): void {
    patchState(this._onYouSignalState, {
      ...WAITING_ON_YOU_DEFAULT_SIGNAL_STATE,
    });
  }

  /**
   * Raises a dismissible snackbar for a non-blocking, informational message.
   *
   * Used by the "Waiting on you" card when a user action cannot be completed —
   * e.g. a row that carries no navigable identifiers.
   *
   * @param message - Display copy shown to the user.
   * @example
   * this._waitingOnYouSignalState.displayToast('Can not navigate to Cardio Phase II');
   */
  displayToast(message: string): void {
    this._snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  loadErrorState() {
    patchState(this._onYouSignalState, {
      isLoading: false,
      error: new Error('Error State Loading'),
    });
  }

  loadEmptyState() {
    patchState(this._onYouSignalState, {
      isLoading: false,
      error: null,
      data: null,
    });
  }

  loadLoadingState() {
    patchState(this._onYouSignalState, {
      isLoading: true,
      error: null,
      data: null,
    });
  }
}
